/*
 * The workspace's own import, said in one line above the list (LEA-483; plan D-6,
 * docs/2026-11-02-lea-483-484-import-owner-and-progress-plan.md): while this workspace's last 90 days
 * of mail are still coming in, the list is incomplete, and this says why, to everyone who reads it.
 * The count is campaigns done of total, the only honest one (Bison gives no total inside a campaign).
 *
 * It wears the history note's look (`import-note.tsx`): ring, words, count, thin bar. The line is no
 * live region; a polite one beside it speaks at a quarter of campaigns passed and once at the end,
 * so a 15-second poll never talks over the rep. A load that arrives mid-import, or that finds the
 * import already complete, says nothing. Once finished it says so for 4 seconds, then goes.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { appCount } from "../../src/app-dates.ts";
import { type ImportState, importMilestone, importPercent } from "../../src/workspace-import.ts";
import { CheckIcon } from "../icons.tsx";
import { importStatusQuery } from "../queries.ts";
import { ImportRing } from "./import-note.tsx";

/** How long the line says the import finished before it goes. */
const FINISHED_MS = 4_000;
/** How long an announcement stays in the polite region, so the same words can be said again. */
const SPOKEN_MS = 6_000;

const FINISHED_ANNOUNCEMENT = "Import finished. Every campaign's last 90 days of mail are in.";
const milestoneAnnouncement = (milestone: 25 | 50 | 75) =>
  `Importing: ${milestone}% of campaigns done.`;

/** "3 of 7 campaigns done": the line's count, and an empty list's. */
export function campaignsDoneText(done: number, total: number): string {
  return `${appCount(done)} of ${appCount(total)} ${total === 1 ? "campaign" : "campaigns"} done`;
}

/** What the line shows: the import while it runs, its finish for a moment, else nothing. */
type ImportLine = Exclude<ImportState, { state: "complete" }> | { state: "finished" };

export type WorkspaceImport = {
  /** The line above the list; null draws none, and the history note keeps its place. */
  readonly line: ImportLine | null;
  /** The import while it runs, for an empty list to say why it is empty; null otherwise. */
  readonly running: Exclude<ImportState, { state: "complete" }> | null;
  /** What the polite region says now; empty between moments. */
  readonly announcement: string;
};

/**
 * This workspace's import, read as `importStatusQuery` polls it, and the moments worth saying once:
 * a quarter passed, or the import finished, from one read to the next in this workspace. A read that
 * finds a finished workspace importing again (a new campaign, plan D-5) shows the line silently.
 */
export function useWorkspaceImport(workspaceId: string): WorkspaceImport {
  const current = useQuery(importStatusQuery(workspaceId)).data ?? null;
  const [finishedIn, setFinishedIn] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const previous = useRef<{ workspaceId: string; state: ImportState } | null>(null);
  useEffect(() => {
    if (!current) return;
    const before = previous.current?.workspaceId === workspaceId ? previous.current.state : null;
    previous.current = { workspaceId, state: current };
    if (!before || before.state === "complete") return;
    if (current.state === "complete") {
      setFinishedIn(workspaceId);
      setAnnouncement(FINISHED_ANNOUNCEMENT);
      return;
    }
    const milestone = importMilestone(current);
    if (milestone !== 0 && milestone > importMilestone(before))
      setAnnouncement(milestoneAnnouncement(milestone));
  }, [current, workspaceId]);
  useEffect(() => {
    if (!finishedIn) return;
    const timer = setTimeout(() => setFinishedIn(null), FINISHED_MS);
    return () => clearTimeout(timer);
  }, [finishedIn]);
  useEffect(() => {
    if (!announcement) return;
    const timer = setTimeout(() => setAnnouncement(""), SPOKEN_MS);
    return () => clearTimeout(timer);
  }, [announcement]);
  const running = current && current.state !== "complete" ? current : null;
  const finished = finishedIn === workspaceId && current?.state === "complete";
  return {
    line: running ?? (finished ? { state: "finished" } : null),
    running,
    announcement,
  };
}

function Line(props: { line: ImportLine }) {
  const { line } = props;
  switch (line.state) {
    case "finding":
      return (
        <div className="lcr-import-note" data-state="finding">
          <ImportRing />
          <strong className="lcr-import-note-label">Finding this workspace's campaigns…</strong>
        </div>
      );
    case "importing":
      // The flex gap draws the spaces; the spaces themselves keep the line's text one sentence. "of
      // mail" stays in the title: at the list's default 400 px it pushed the words into an ellipsis.
      return (
        <div className="lcr-import-note" data-state="importing">
          <ImportRing /> <strong className="lcr-import-note-label">Importing</strong>{" "}
          <span className="lcr-import-note-name" title="The last 90 days of mail">
            the last 90 days
          </span>{" "}
          <span className="lcr-import-note-count">{campaignsDoneText(line.done, line.total)}</span>
          <span className="lcr-import-note-bar" aria-hidden="true">
            <span style={{ width: `${Math.max(importPercent(line), 4)}%` }} />
          </span>
        </div>
      );
    case "finished":
      return (
        <div className="lcr-import-note" data-state="done">
          <CheckIcon size={14} />
          <strong className="lcr-import-note-label">Import finished</strong>
        </div>
      );
  }
}

/**
 * The line and its polite region. The region is drawn whether or not the line is, so it is already
 * in the page when it first has something to say.
 */
export function WorkspaceImportNote(props: { workspaceImport: WorkspaceImport }) {
  const { line, announcement } = props.workspaceImport;
  return (
    <>
      {line && <Line line={line} />}
      <p className="lcr-visually-hidden" role="status">
        {announcement}
      </p>
    </>
  );
}
